import { Injectable, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { StorageService } from './storage.service';
import { ChatDataService } from './chat-data.service';
import { FcmService } from './fcm.service';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
  private socket: Socket | null = null;
  private user: any = null;
  private wsSubscription: Subscription | null = null;
  // Messages sent before the handshake finishes (e.g. a component mounting
  // right as the page loads, racing the socket's own connect()) used to be
  // silently dropped by handleOutgoing's `!this.socket.connected` guard --
  // this is exactly what caused "everyone shows offline until someone else
  // reconnects": the initial GET_CONNECTED_USERS request raced the
  // handshake and lost, with nothing to retry it. Queue instead, flush on connect.
  private outgoingQueue: any[] = [];
  // Guards against an intermittent dev-mode failure mode (seen as "WebSocket
  // is closed before the connection is established" in devtools) where the
  // handshake dies mid-flight and socket.io's own reconnection logic doesn't
  // pick it back up -- the socket just sits there forever not-connected,
  // with nothing else to recover it short of a manual page refresh.
  private connectWatchdog: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private chatDataService: ChatDataService,
    private fcmService: FcmService,
  ) {
    this.user = StorageService.getItem('self');
  }

  public connect() {
    this.user = StorageService.getItem('self');

    if (!this.user || !['EMPLOYEE', 'ADMIN'].includes(this.user?.user_type)) return;
    if (this.socket && this.socket.connected) return;

    const userId = this.user?.user_id || this.user?.id;

    if (!userId) return;

    // Stored profile_image is a relative key (same as everywhere else this
    // app renders an avatar, e.g. chat.component.html's `s3_bucket_url+...`)
    // -- resolve it to a real URL here so it's directly usable as a push
    // notification icon.
    const profileImage = this.user?.profile_image
      ? (/^https?:\/\//.test(this.user.profile_image)
          ? this.user.profile_image
          : environment.s3_bucket_url + this.user.profile_image)
      : null;

    const isEmployee = this.user?.user_type === 'EMPLOYEE';
    // ADMIN has no department/chat-visibility restriction (always sees
    // everyone, same as a DEFAULT-chat-type org) -- this connection exists
    // only to receive cross-system alerts (Leave/Ticket notifications), not
    // to chat, so there's no department scoping to carry over.
    const departmentIds = isEmployee
      ? Array.from(
          new Set(
            [this.user?.department_id, ...(this.user?.head_department_ids || [])]
              .filter((id: any) => id !== null && id !== undefined)
              .map((id: any) => String(id))
          )
        )
      : [];

    this.socket = io(environment.chat_socket_url, {
      transports: ['websocket'],
      auth: {
        user: {
          id: String(userId),
          projectId: environment.chat_project_id || null,
          name: this.user?.name || '',
          profileImage,
          userType: this.user?.user_type,
          chatType: isEmployee ? (this.user?.chat_type || 'DEFAULT') : 'DEFAULT',
          departmentIds,
        },
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // No reconnectionAttempts cap -- socket.io's default backoff (1s up to
      // a 5s max) means a 100-attempt cap gives up after ~8 minutes. A tab
      // left open across a backend restart/deploy longer than that would
      // then show everyone offline permanently until manually refreshed
      // (exactly what happened during today's testing). Keep retrying
      // indefinitely instead -- it's cheap, and reconnecting is always safe.
    });

    // 7s was too aggressive -- on a loaded dev machine a legitimate handshake
    // can take nearly that long, and this timer raced the real 'connect'
    // event and won, killing a connection that was about to succeed (seen
    // directly in devtools: a clean server ACK immediately followed by a
    // client-initiated disconnect packet). 25s gives real connections far
    // more room while still recovering a genuinely stuck one automatically.
    if (this.connectWatchdog) clearTimeout(this.connectWatchdog);
    this.connectWatchdog = setTimeout(() => this.checkStuckConnection(), 25000);

    this.socket.on('connect', () => {
      if (this.connectWatchdog) {
        clearTimeout(this.connectWatchdog);
        this.connectWatchdog = null;
      }
      this.chatDataService.sendFromWebsocket({ type: 'CONNECTED' });
      this.fcmService.init();
      this.flushOutgoingQueue();
    });

    // Server pushes unread counts after onConnection completes
    this.socket.on('unread-counts', (counts: any[]) => {
      this.chatDataService.sendFromWebsocket({ type: 'UNREAD_COUNTS', data: counts });
    });

    this.socket.on('general-chat-id', (data: any) => {
      this.chatDataService.setGeneralChatId(data?.chat_id || null);
      this.chatDataService.sendFromWebsocket({ type: 'GENERAL_CHAT_ID', data });
    });

    this.socket.on('disconnect', () => {
      this.chatDataService.sendFromWebsocket({ type: 'DISCONNECTED' });
    });

    this.socket.on('connect_error', () => {});

    this.socket.on('new-message', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'NEW_MESSAGE', data });
    });

    this.socket.on('seen-messages', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'SEEN_MESSAGES', data });
    });

    this.socket.on('connected-users', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'CONNECTED_USERS', data });
    });

    this.socket.on('new-user-connected', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'USER_CONNECTED', data });
    });

    this.socket.on('user-disconnected', (id: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'USER_DISCONNECTED', data: id });
    });

    // Live push to this same user's OTHER devices/tabs when a chat is
    // deleted on one of them -- without this, those other sessions only
    // found out on their next list refresh or reconnect.
    this.socket.on('chat-deleted', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'CHAT_DELETED', data });
    });

    this.socket.on('message-edited', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'MESSAGE_EDITED', data });
    });

    this.socket.on('message-deleted', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'MESSAGE_DELETED', data });
    });

    this.socket.on('group-deleted', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'GROUP_DELETED', data });
    });

    this.socket.on('member-removed', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'MEMBER_REMOVED', data });
    });

    this.socket.on('reaction-added', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'REACTION_ADDED', data });
    });

    this.socket.on('messages-pinned', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'MESSAGES_PINNED', data });
    });

    this.socket.on('messages-unpinned', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'MESSAGES_UNPINNED', data });
    });

    this.socket.on('new-group', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'NEW_GROUP', data });
    });

    this.socket.on('group-members-updated', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'GROUP_MEMBERS_UPDATED', data });
    });

    this.socket.on('user-typing', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'USER_TYPING', data });
    });

    this.socket.on('user-stop-typing', (data: any) => {
      this.chatDataService.sendFromWebsocket({ type: 'USER_STOP_TYPING', data });
    });

    // Sent by the backend only when the recipient is online but doesn't have
    // this chat open (an offline recipient gets an FCM push instead). The
    // server sends this with a timeout+ack: it falls back to FCM for this
    // device if we don't acknowledge within a few seconds, since "online" in
    // its presence map doesn't guarantee this socket is actually alive and
    // processing events right now (e.g. a dead connection its heartbeat
    // hasn't caught yet) -- so this ack is what makes delivery reliable.
    this.socket.on('chat-notification', (data: any, ack?: (response: any) => void) => {
      this.chatDataService.sendFromWebsocket({ type: 'CHAT_NOTIFICATION', data });
      if (typeof ack === 'function') ack({ received: true });
    });

    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
    }

    this.wsSubscription = this.chatDataService.toWebsocket$.subscribe(
      (msg: any) => this.handleOutgoing(msg)
    );
  }

  private checkStuckConnection(): void {
    this.connectWatchdog = null;
    if (!this.socket || this.socket.connected) return;
    // socket.io's own reconnection logic hasn't recovered this one -- tear
    // it down completely and start over with a brand new socket rather than
    // leaving it in limbo (which otherwise persists until a manual refresh).
    const stale = this.socket;
    this.socket = null;
    stale.removeAllListeners();
    stale.disconnect();
    this.connect();
  }

  private flushOutgoingQueue(): void {
    if (!this.outgoingQueue.length) return;
    const queued = this.outgoingQueue;
    this.outgoingQueue = [];
    queued.forEach((msg) => this.handleOutgoing(msg));
  }

  private handleOutgoing(msg: any) {
    if (!msg || !msg.type) return;
    if (!this.socket || !this.socket.connected) {
      this.outgoingQueue.push(msg);
      return;
    }

    switch (msg.type) {
      case 'GET_CHAT_ID':
        this.socket.emit('get-chat-id', { user_id: msg.user_id }, (resp: any) => {
          this.chatDataService.sendFromWebsocket({
            type: 'CHAT_ID_RESOLVED',
            data: {
              partner_id: msg.user_id,
              chat_id: resp ? resp.chat_id : null,
            },
          });
        });
        break;

      case 'SEND_MESSAGE':
        this.socket.emit('send-message', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({
            type: 'MESSAGE_SENT',
            data: { request: msg.data, response: resp },
          });
        });
        break;

      case 'SEEN_MESSAGE':
        this.socket.emit('seen-message', msg.data);
        break;

      case 'GET_CONNECTED_USERS':
        this.socket.emit('get-connected-users');
        break;

      case 'TYPING':
        this.socket.emit('typing', msg.data);
        break;

      case 'STOP_TYPING':
        this.socket.emit('stop-typing', msg.data);
        break;

      case 'ACTIVE_CHAT':
        this.socket.emit('active-chat', msg.data);
        break;

      case 'GROUP_CREATED':
        this.socket.emit('group-created', msg.data);
        break;

      case 'EDIT_MESSAGE':
        this.socket.emit('edit-message', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'MESSAGE_EDIT_RESULT', data: resp });
        });
        break;

      case 'DELETE_MESSAGE_FOR_ME':
        this.socket.emit('delete-message-for-me', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'DELETE_FOR_ME_RESULT', data: resp });
        });
        break;

      case 'DELETE_MESSAGE_FOR_ALL':
        this.socket.emit('delete-message-for-all', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'DELETE_FOR_ALL_RESULT', data: resp });
        });
        break;

      case 'DELETE_CHAT':
        this.socket.emit('delete-chat', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'DELETE_CHAT_RESULT', data: { ...msg.data, success: resp } });
        });
        break;

      case 'UPDATE_GROUP_IMAGE':
        this.socket.emit('update-group-image', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'GROUP_IMAGE_UPDATED', data: resp });
        });
        break;

      case 'MAKE_ADMIN':
        this.socket.emit('make-admin', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'ADMIN_RESULT', data: resp });
        });
        break;

      case 'REMOVE_ADMIN':
        this.socket.emit('remove-admin', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'ADMIN_RESULT', data: resp });
        });
        break;

      case 'ADD_MEMBER':
        this.socket.emit('add-member', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'ADD_MEMBER_RESULT', data: resp });
        });
        break;

      case 'REMOVE_MEMBER':
        this.socket.emit('remove-member', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'REMOVE_MEMBER_RESULT', data: resp });
        });
        break;

      case 'UPDATE_GROUP_DESCRIPTION':
        this.socket.emit('update-group-description', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'GROUP_UPDATED', data: resp });
        });
        break;

      case 'UPDATE_GROUP_NAME':
        this.socket.emit('update-group-name', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'GROUP_UPDATED', data: resp });
        });
        break;

      case 'DELETE_GROUP':
        this.socket.emit('delete-group', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'DELETE_GROUP_RESULT', data: resp });
        });
        break;

      case 'GET_RECENT_MESSAGE':
        this.socket.emit('get-recent-message', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'RECENT_MESSAGE', data: resp });
        });
        break;

      case 'GET_MESSAGES':
        this.socket.emit('get-messages', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'MESSAGES_LOADED', data: resp });
        });
        break;

      case 'PIN_MESSAGES':
        this.socket.emit('pin-messages', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'PIN_RESULT', data: { ok: resp, chat_id: msg.data?.chat_id } });
        });
        break;

      case 'UNPIN_MESSAGES':
        this.socket.emit('unpin-messages', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'UNPIN_RESULT', data: { ok: resp, chat_id: msg.data?.chat_id } });
        });
        break;

      case 'GET_PINNED_MESSAGES':
        this.socket.emit('get-pinned-messages', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'PINNED_LOADED', data: resp });
        });
        break;

      case 'CREATE_GROUP':
        this.socket.emit('create-group', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'GROUP_CREATED', data: resp });
        });
        break;

      case 'GET_USER_GROUPS':
        this.socket.emit('get-user-groups', {}, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'GROUPS_LOADED', data: resp });
        });
        break;

      case 'GET_GENERAL_CHAT_ID':
        this.socket.emit('get-general-chat-id', {}, (resp: any) => {
          if (resp?.chat_id) {
            this.chatDataService.setGeneralChatId(resp.chat_id);
            this.chatDataService.sendFromWebsocket({ type: 'GENERAL_CHAT_ID', data: resp });
          }
        });
        break;

      case 'ADD_REACTION':
        this.socket.emit('add-reaction', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'REACTION_RESULT', data: resp });
        });
        break;

      // Slack-style "Pause notifications for..." -- a global mute window
      // checked server-side by every notification path before it ever shows
      // a banner or sends a push.
      case 'SET_DND':
        this.socket.emit('set-dnd', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'DND_STATUS', data: resp ? { dndUntil: resp.dndUntil } : { dndUntil: null } });
        });
        break;

      case 'CLEAR_DND':
        this.socket.emit('clear-dnd', {}, () => {
          this.chatDataService.sendFromWebsocket({ type: 'DND_STATUS', data: { dndUntil: null } });
        });
        break;

      case 'GET_DND_STATUS':
        this.socket.emit('get-dnd-status', {}, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'DND_STATUS', data: { dndUntil: resp?.dndUntil ?? null } });
        });
        break;

      // Slack-style "Set yourself as away" -- hides this user from everyone
      // else's online/offline presence view (see the Node backend's
      // getConnectedUsers()) without touching actual connection state.
      case 'SET_MANUAL_STATUS':
        this.socket.emit('set-manual-status', msg.data, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'MANUAL_STATUS', data: { status: resp?.status || 'active' } });
        });
        break;

      case 'GET_MANUAL_STATUS':
        this.socket.emit('get-manual-status', {}, (resp: any) => {
          this.chatDataService.sendFromWebsocket({ type: 'MANUAL_STATUS', data: { status: resp?.status || 'active' } });
        });
        break;

      default:
        break;
    }
  }

  public close() {
    if (this.connectWatchdog) {
      clearTimeout(this.connectWatchdog);
      this.connectWatchdog = null;
    }

    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.outgoingQueue = [];
  }

  ngOnDestroy(): void {
    this.close();
  }
}
